import {useMutation, type UseMutationResult} from '@github-ui/react-query'
import {verifiedFetch} from '@github-ui/verified-fetch'

export type CreateFixWorkflowJobRequest = {
  problemStatement: string
  baseRef: string
  eventType: string
  eventUrl?: string
  eventContent: string
  checkRunId?: number
}

export type CreateFixWorkflowJobResponse = {
  success: boolean
  redirectUrl?: string
}

export type CreateFixWorkflowJobOptions = {
  /** The path to the agent session creation endpoint (e.g., from Rails) */
  createAgentSessionPath?: string
  /** Repository owner - used to construct path when createAgentSessionPath is not provided */
  owner?: string
  /** Repository name - used to construct path when createAgentSessionPath is not provided */
  repo?: string
  onSuccess?: (data: CreateFixWorkflowJobResponse) => void
  onError?: (error: Error) => void
}

export function useCreateFixWorkflowJob({
  createAgentSessionPath,
  owner,
  repo,
  onSuccess,
  onError,
}: CreateFixWorkflowJobOptions): UseMutationResult<CreateFixWorkflowJobResponse, Error, CreateFixWorkflowJobRequest> {
  const createJob = useMutation({
    mutationFn: async (request: CreateFixWorkflowJobRequest) => {
      // Determine the endpoint path
      // Route is /:user_id/:repository/agent-sessions (note: hyphen, not underscore)
      const path = createAgentSessionPath || (owner && repo ? `/${owner}/${repo}/agent-sessions` : null)

      if (!path) {
        throw new Error('Either createAgentSessionPath or owner/repo must be provided')
      }

      const formData = new FormData()
      formData.append('problem_statement', request.problemStatement)
      formData.append('base_ref', request.baseRef)
      formData.append('event_type', request.eventType)
      formData.append('event_content', request.eventContent)
      if (request.eventUrl) {
        formData.append('event_url', request.eventUrl)
      }
      if (request.checkRunId !== undefined) {
        // Send as both param names for backward compatibility during deploy
        formData.append('check_run_id', String(request.checkRunId))
        formData.append('workflow_run_id', String(request.checkRunId))
      }

      const response = await verifiedFetch(path, {
        method: 'POST',
        body: formData,
      })

      if (response.redirected) {
        return {success: true, redirectUrl: response.url}
      }

      if (!response.ok) {
        throw new Error(`Failed to create agent session: ${response.status}`)
      }

      return {success: true}
    },
    onSuccess,
    onError,
  })

  return createJob
}
